/**
 * 移动端分类列表页
 */
import React, { useEffect, useState } from 'react';
import {
  List,
  PullToRefresh,
  Empty,
  Skeleton,
  Dialog,
  Button,
  Form,
  Input,
  TextArea,
  Stepper,
  Selector,
} from 'antd-mobile';
import { adminProductApi } from '../../api/index';
import Toast from 'antd-mobile/es/components/toast';

export default function MobileCategoryListPage() {
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [formModalVisible, setFormModalVisible] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const data = await adminProductApi.listCategories();
      setCategories(data || []);
    } catch (err) {
      Toast.show('获取分类列表失败');
    } finally {
      setLoading(false);
    }
  };

  const refreshData = async () => {
    await fetchCategories();
  };

  const openForm = (category) => {
    setEditingCategory(category);
    if (category) {
      form.setFieldsValue({
        name: category.name,
        slug: category.slug,
        description: category.description,
        imageUrl: category.imageUrl,
        sortOrder: category.sortOrder,
        parentId: category.parentId,
      });
    } else {
      form.resetFields();
    }
    setFormModalVisible(true);
  };

  const handleSubmit = async (values) => {
    try {
      const payload = {
        ...values,
        parentId: values.parentId || null,
      };
      if (editingCategory) {
        await adminProductApi.updateCategory(editingCategory.id, payload);
        Toast.show('更新成功');
      } else {
        await adminProductApi.createCategory(payload);
        Toast.show('创建成功');
      }
      setFormModalVisible(false);
      fetchCategories();
    } catch (err) {
      Toast.show(err?.response?.data?.message || '操作失败');
    }
  };

  const handleDelete = (id) => {
    Dialog.confirm({
      content: '确定要删除此分类吗？',
      onConfirm: async () => {
        try {
          await adminProductApi.deleteCategory(id);
          Toast.show('删除成功');
          fetchCategories();
        } catch (err) {
          Toast.show(err?.response?.data?.message || '删除失败');
        }
      },
    });
  };

  if (loading) {
    return (
      <div style={{ padding: 16 }}>
        <Skeleton.Title rows={3} />
        <Skeleton.Title rows={5} style={{ marginTop: 16 }} />
        <Skeleton.Title rows={5} style={{ marginTop: 16 }} />
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f5f5f5', padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>分类管理</h2>
        <Button size="small" color="primary" onClick={() => openForm(null)}>
          新建
        </Button>
      </div>

      <PullToRefresh onRefresh={refreshData}>
        {categories.length > 0 ? (
          <List>
            {categories.map((category) => (
              <List.Item
                key={category.id}
                style={{
                  background: '#fff',
                  marginBottom: 12,
                  padding: '12px 16px',
                }}
                description={
                  <div style={{ fontSize: 13, color: '#999', marginTop: 4 }}>
                    {category.description?.slice(0, 30) || '暂无描述'}
                  </div>
                }
                extra={
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <span
                      style={{ fontSize: 13, color: '#1677ff', cursor: 'pointer' }}
                      onClick={() => openForm(category)}
                    >
                      编辑
                    </span>
                    <span
                      style={{ fontSize: 13, color: '#ff4d4f', cursor: 'pointer' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(category.id);
                      }}
                    >
                      删除
                    </span>
                  </div>
                }
              >
                <div style={{ fontSize: 15, fontWeight: 500 }}>{category.name}</div>
                <div style={{ fontSize: 13, color: '#999' }}>/{category.slug}</div>
              </List.Item>
            ))}
          </List>
        ) : (
          <div style={{ padding: '80px 0' }}>
            <Empty description="暂无分类" />
          </div>
        )}
      </PullToRefresh>

      {/* 新建/编辑弹窗 */}
      <Dialog
        visible={formModalVisible}
        style={{ '--content-width': '90%' }}
        content={
          <div style={{ padding: '16px 0' }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>
              {editingCategory ? '编辑分类' : '新建分类'}
            </h3>
            <Form
              form={form}
              layout="vertical"
              footer={
                <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
                  <Button block type="submit" color="primary" onClick={() => form.submit()}>
                    保存
                  </Button>
                  <Button block variant="outline" onClick={() => setFormModalVisible(false)}>
                    取消
                  </Button>
                </div>
              }
              onFinish={handleSubmit}
            >
              <Form.Item name="name" label="分类名称" rules={[{ required: true }]}>
                <Input placeholder="请输入分类名称" />
              </Form.Item>
              <Form.Item name="slug" label="URL 别名">
                <Input placeholder="自定义 URL 路径" />
              </Form.Item>
              <Form.Item name="description" label="描述">
                <TextArea placeholder="请输入分类描述" rows={2} />
              </Form.Item>
              <Form.Item name="imageUrl" label="图片 URL">
                <Input placeholder="请输入图片 URL" />
              </Form.Item>
              <Form.Item name="sortOrder" label="排序" initialValue={0}>
                <Stepper min={0} />
              </Form.Item>
              <Form.Item name="parentId" label="父分类">
                <Selector
                  options={[
                    { label: '无（作为顶级分类）', value: null },
                    ...categories
                      .filter(c => !editingCategory || c.id !== editingCategory.id)
                      .map(c => ({ label: c.name, value: c.id })),
                  ]}
                  placeholder="选择父分类（可选）"
                />
              </Form.Item>
            </Form>
          </div>
        }
        closeOnMaskClick={false}
      />
    </div>
  );
}
